import {
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Box,
  Link,
  Switch,
  FormControlLabel,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import React, { useContext, useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import { useForm, Controller } from "react-hook-form";
import axios from "../../axiosConfig";
import { UserDetailsContext } from "../../Context/userDetailsContext";
import { useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";

const LoginForm = () => {
  const { setUserDetails, userDetails } = useContext(UserDetailsContext);
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    setError,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      idNumber: "", // Ensure default values are provided
      password: "",
      rememberMe: true,
    },
  });

  const nav = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onPasswordClicked = () => {
    setShowPassword((prevState) => !prevState);
  };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const formData = new URLSearchParams();
      formData.append("username", data.idNumber);
      formData.append("password", data.password);
      formData.append("rememberMe", data.rememberMe);

      const userData = await axios.post("/Auth/login", formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true,
      });

      setIsLoading(false);
      setUserDetails({ ...userData, isAuthenticated: true });
      nav("/");
    } catch (e) {
      setIsLoading(false);
      const errorMessage = e?.message || e?.error || t('login_error');
      setError("password", { message: errorMessage });
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{ width: "100%", display: "flex", justifyContent: "center" }}
      
    >
      <Stack sx={{ p: 3, width: isMobile ? "100%" : "75%", maxWidth: 380, gap: 2 }}>
        <Typography
          sx={{ fontWeight: "bold", alignSelf: "center" }}
          color="primary.main"
          variant="h5"
        >
          {t('login_userLogin')}
        </Typography>
        <Stack>
          <Typography sx={{ fontWeight: "bold" }} px={1.3}
          py={.3}>{t('login_idNumber')}</Typography>
          <Controller
            name="idNumber"
            control={control}
            rules={{
              required: t('login_idRequired'),
              pattern: {
                value: /^[a-zA-Z0-9]{6,10}$/,
                message: t('login_idInvalid'),
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                variant="standard"
                sx={{
                  "& .MuiInputBase-root": {
                    padding: 0,
                    border: "1px solid #E4E9EF",
                    borderRadius: "14px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    filter: "drop-shadow(0px 3px 3px rgba(0,0,0,0.16))",
                  },
                  "& input": {
                    border: "none !important",
                    filter: "none !important",
                    boxShadow: "none !important",
                    borderRadius: "0 !important",
                  },
                  "& .MuiInputAdornment-root": { m: 0 },
                }}
                inputProps={{
                  style: {
                    textAlign: "center",
                    height: 48,
                    padding: 0,
                  },
                }}
                InputProps={{
                  disableUnderline: true,
                  placeholder: t('login_idPlaceholder'),
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 48,
                          height: 48,
                          backgroundColor: "#EAF0F7",
                        }}
                      >
                        <PersonIcon color="primary" />
                      </Box>
                    </InputAdornment>
                  ),
                }}
                FormHelperTextProps={{
                  sx: {
                    textAlign: "start",
                  },
                }}
                error={!!errors.idNumber}
                helperText={errors.idNumber ? errors.idNumber.message : ""}
              />
            )}
          />
        </Stack>
        <Stack>
          <Typography sx={{ fontWeight: "bold" }}px={1.3}
          py={.3}>{t('login_password')}</Typography>
          <Controller
            name="password"
            control={control}
            rules={{
              required: t('login_passwordRequired'),
              minLength: {
                value: 3,
                message: t('login_passwordMinLength'),
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                type={showPassword ? "text" : "password"}
                variant="standard"
                sx={{
                  "& .MuiInputBase-root": {
                    padding: 0,
                    border: "1px solid #E4E9EF",
                    borderRadius: "14px",
                    overflow: "hidden",
                    backgroundColor: "#fff",
                    filter: "drop-shadow(0px 3px 3px rgba(0,0,0,0.16))",
                  },
                  "& input": {
                    border: "none !important",
                    filter: "none !important",
                    boxShadow: "none !important",
                    borderRadius: "0 !important",
                  },
                  "& .MuiInputAdornment-root": { m: 0 },
                }}
                inputProps={{
                  style: {
                    textAlign: "center",
                    height: 48,
                    padding: 0,
                  },
                }}
                InputProps={{
                  disableUnderline: true,
                  placeholder: t('login_passwordPlaceholder'),
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 48,
                          height: 48,
                          backgroundColor: "#EAF0F7",
                        }}
                      >
                        <IconButton onClick={onPasswordClicked}>
                          {showPassword ? (
                            <VisibilityOffIcon color="primary" />
                          ) : (
                            <VisibilityIcon color="primary" />
                          )}
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                FormHelperTextProps={{
                  sx: {
                    textAlign: "start",
                  },
                }}
                error={!!errors.password}
                helperText={errors.password ? errors.password.message : ""}
              />
            )}
          />
        </Stack>
    <Stack
  direction="row"
  alignItems="center"
  justifyContent="space-between"
  px={{ xs: 1, sm: 2.4 }} // Responsive padding
  spacing={1} // Add spacing between elements
  sx={{
    flexWrap: { xs: 'nowrap' }, // Prevent wrapping on mobile
    gap: { xs: 1, sm: 2 } // Responsive gap
  }}
>
  <Link 
    href="/forget-password"
    sx={{
      fontSize: { xs: '0.875rem', sm: '1rem' }, // Smaller font on mobile
      whiteSpace: 'nowrap' // Prevent text wrapping
    }}
  >
    {t('login_forgotPassword')}
  </Link>
  
  <Box 
    sx={{ 
      display: "flex", 
      alignItems: "center",
      gap: 0.5 // Small gap between text and switch
    }}
  >
    <Typography 
      variant="body2"
      sx={{ 
        fontSize: { xs: '0.875rem', sm: '1rem' },
        whiteSpace: 'nowrap'
      }}
    >
      {t('login_rememberMe')}
    </Typography>
    <Controller
      name="rememberMe"
      control={control}
      render={({ field }) => (
        <Switch
          {...field}
          onChange={field.onChange}
          checked={field.value}
          size="small" // Make switch smaller on mobile
        />
      )}
    />
  </Box>
</Stack>
        <LoadingButton
          type="submit"
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
          sx={{ width: "80%", alignSelf: "center", borderRadius: "14px" }}
          variant="contained"
        >
          <Typography>{t('login_submit')}</Typography>
        </LoadingButton>
      </Stack>
    </form>
  );
};

export default LoginForm;
